import {
  type BoardSize,
  DEFAULT_BOARD_SIZE,
  GameMode,
  MAX_BOARD_CELLS,
  MAX_HISCORES,
  MIN_BOARD_CELLS,
  NUM_GEM_TYPES,
} from "./constants";
import type { SavedGame } from "./engine";

/**
 * Browser persistence for settings, high scores, and the saved game. The original stored these
 * with GSettings, libgames-support, and a file in the user config directory.
 */

const SETTINGS_KEY = "gweled.settings";
const SCORES_KEY = "gweled.hiscores";
const SAVED_GAME_KEY = "gweled.saved-game";
const BOARD_SIZE_KEY = "gweled.board-size";

export interface Settings {
  sound: boolean;
  hints: boolean;
  /** Sound effect volume from 0 (silent) to 1 (full). */
  volume: number;
}

export const DEFAULT_SETTINGS: Settings = { sound: true, hints: true, volume: 1 };

/** High score categories. Endless games never end, so they are not recorded. */
export type ScoreCategory = typeof GameMode.Normal | typeof GameMode.Timed;
export const SCORE_CATEGORIES: readonly ScoreCategory[] = [GameMode.Normal, GameMode.Timed];

export interface ScoreEntry {
  score: number;
  name: string;
  /** ISO 8601 timestamp. */
  date: string;
}

type ScoreTables = Record<ScoreCategory, ScoreEntry[]>;

function readJson(key: string): unknown {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? null : (JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be full or disabled. The game keeps working without persistence.
  }
}

export function loadSettings(): Settings {
  const data = readJson(SETTINGS_KEY);
  if (typeof data !== "object" || data === null) return { ...DEFAULT_SETTINGS };
  const record = data as Partial<Settings>;
  return {
    sound: typeof record.sound === "boolean" ? record.sound : DEFAULT_SETTINGS.sound,
    hints: typeof record.hints === "boolean" ? record.hints : DEFAULT_SETTINGS.hints,
    volume:
      typeof record.volume === "number" && Number.isFinite(record.volume)
        ? Math.min(1, Math.max(0, record.volume))
        : DEFAULT_SETTINGS.volume,
  };
}

export function saveSettings(settings: Settings): void {
  writeJson(SETTINGS_KEY, settings);
}

/** Keeps a saved width or height inside the allowed range, or falls back to the default. */
function readCellCount(value: unknown, fallback: number): number {
  if (typeof value !== "number" || !Number.isInteger(value)) return fallback;
  return Math.min(MAX_BOARD_CELLS, Math.max(MIN_BOARD_CELLS, value));
}

export function loadBoardSize(): BoardSize {
  const data = readJson(BOARD_SIZE_KEY);
  if (typeof data !== "object" || data === null) return { ...DEFAULT_BOARD_SIZE };
  const record = data as Record<string, unknown>;
  return {
    width: readCellCount(record.width, DEFAULT_BOARD_SIZE.width),
    height: readCellCount(record.height, DEFAULT_BOARD_SIZE.height),
  };
}

export function saveBoardSize(size: BoardSize): void {
  writeJson(BOARD_SIZE_KEY, size);
}

function isScoreEntry(value: unknown): value is ScoreEntry {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.score === "number" &&
    typeof entry.name === "string" &&
    typeof entry.date === "string"
  );
}

export function loadScores(): ScoreTables {
  const data = readJson(SCORES_KEY);
  const tables: ScoreTables = { normal: [], timed: [] };
  if (typeof data !== "object" || data === null) return tables;

  for (const category of SCORE_CATEGORIES) {
    const list = (data as Record<string, unknown>)[category];
    if (Array.isArray(list)) {
      tables[category] = list.filter(isScoreEntry).slice(0, MAX_HISCORES);
    }
  }
  return tables;
}

/** True if `score` would appear in the top list for `category`. */
export function qualifiesForHiscores(category: ScoreCategory, score: number): boolean {
  if (score <= 0) return false;
  const list = loadScores()[category];
  if (list.length < MAX_HISCORES) return true;
  return score > list[list.length - 1].score;
}

/** Adds a score to the top list and returns its index, or -1 if it did not make the list. */
export function addHiscore(category: ScoreCategory, score: number, name: string): number {
  const tables = loadScores();
  const list = tables[category];

  let index = list.findIndex((entry) => entry.score < score);
  if (index === -1) index = list.length;
  if (index >= MAX_HISCORES) return -1;

  list.splice(index, 0, { score, name, date: new Date().toISOString() });
  tables[category] = list.slice(0, MAX_HISCORES);
  writeJson(SCORES_KEY, tables);
  return index;
}

const MODES: ReadonlySet<string> = new Set<string>([
  GameMode.Normal,
  GameMode.Timed,
  GameMode.Endless,
]);

function isValidSavedGame(value: unknown): value is SavedGame {
  if (typeof value !== "object" || value === null) return false;
  const game = value as Record<string, unknown>;
  if (typeof game.mode !== "string" || !MODES.has(game.mode)) return false;
  for (const key of [
    "score",
    "totalGemsRemoved",
    "bonusMultiply",
    "previousBonusAt",
    "nextBonusAt",
    "level",
    "stepsForTimer",
  ]) {
    if (typeof game[key] !== "number" || !Number.isFinite(game[key])) return false;
  }
  return isValidBoard(game.board);
}

/** A board is a list of columns that all have the same height, within the allowed sizes. */
function isValidBoard(value: unknown): value is number[][] {
  if (!Array.isArray(value)) return false;
  const columns: unknown[] = value;
  if (!isBoardCellCount(columns.length)) return false;

  const first: unknown = columns[0];
  const height = Array.isArray(first) ? first.length : 0;
  if (!isBoardCellCount(height)) return false;

  return columns.every(
    (column: unknown) =>
      Array.isArray(column) &&
      column.length === height &&
      column.every(
        (type: unknown) => typeof type === "number" && type >= 0 && type < NUM_GEM_TYPES,
      ),
  );
}

function isBoardCellCount(count: number): boolean {
  return count >= MIN_BOARD_CELLS && count <= MAX_BOARD_CELLS;
}

export function saveGame(game: SavedGame): void {
  writeJson(SAVED_GAME_KEY, game);
}

export function loadSavedGame(): SavedGame | null {
  const data = readJson(SAVED_GAME_KEY);
  return isValidSavedGame(data) ? data : null;
}

export function hasSavedGame(): boolean {
  return loadSavedGame() !== null;
}

export function clearSavedGame(): void {
  try {
    window.localStorage.removeItem(SAVED_GAME_KEY);
  } catch {
    // Ignore: nothing to clear when storage is unavailable.
  }
}
